import type React from 'react'
import { useSelector } from 'react-redux'
import PersonalDetailItem from '../../../../../../components/PersonalDetailItem'
import Table from '../../../../../../components/Table/Table'
import { setAffiliateStatsMonthlyTableState } from '../../../../../../redux/slices/tableFiltersSlice'
import type { RootState } from '../../../../../../redux/store'
import type { AffiliateStatsDto } from '../../../../../../types/coreApi-types'

// Reusable component for displaying affiliate stats
export interface AffiliateStatsViewProps {
  affiliateStats?: AffiliateStatsDto
  isLoading: boolean
  title?: string
  showTitle?: boolean
}

export const AffiliateStatsView: React.FC<AffiliateStatsViewProps> = ({
  affiliateStats,
  isLoading,
  title = 'Affiliate Partner Statistics',
  showTitle = true,
}) => {
  // Get table states from Redux store
  const monthlyTableState = useSelector(
    (state: RootState) => state.affiliateStatsMonthlyTable
  )
  if (isLoading) {
    return <div>Loading stats...</div>
  }

  if (!affiliateStats) {
    return <div>Stats not available</div>
  }

  return (
    <div className="space-y-6">
      {showTitle && (
        <div className="card p-6">
          <h1 className="text-2xl font-bold mb-2">{title}</h1>
        </div>
      )}
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4">Performance Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-600 dark:text-blue-400">
              Total Signups
            </h3>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {affiliateStats.totalSignups.toLocaleString()}
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-600 dark:text-green-400">
              Completed KYC
            </h3>
            <p className="text-2xl font-bold text-green-900 dark:text-green-100">
              {affiliateStats.completedKyc.toLocaleString()}
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-purple-600 dark:text-purple-400">
              Active Users
            </h3>
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {affiliateStats.activeUsers.toLocaleString()}
            </p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-orange-600 dark:text-orange-400">
              Users with Transactions
            </h3>
            <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">
              {affiliateStats.usersWithTransactions.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Financial Overview */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4">Financial Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PersonalDetailItem
            title="Total Transaction Volume"
            value={`€${affiliateStats.totalTransactionVolume.toLocaleString(
              'en-US',
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`}
          />
          <PersonalDetailItem
            title="Total Commission Earned"
            value={`€${affiliateStats.totalCommissionEarned.toLocaleString(
              'en-US',
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`}
          />
        </div>
      </div>

      {/* Monthly Breakdown */}
      {affiliateStats.monthlyBreakdown &&
        affiliateStats.monthlyBreakdown.length > 0 && (
          <div className="card p-6 h-[1150px]">
            <div className="h-full">
              <h2 className="text-xl font-semibold mb-4">
                Monthly Performance
              </h2>
              <Table
                tableState={monthlyTableState}
                setTableState={setAffiliateStatsMonthlyTableState}
                navigate={() => null}
                isLoading={false}
                onClickRow={() => {}}
                emptyDataRender={<div>No monthly data available</div>}
                defaultData={[
                  ...affiliateStats.monthlyBreakdown.map((item, index) => ({
                    ...item,
                    id: index.toString(),
                    period: `${item.monthName} ${item.year}`,
                    transactionVolumeFormatted: `€${item.transactionVolume.toLocaleString(
                      'en-US',
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}`,
                    commissionEarnedFormatted: `€${item.commissionEarned.toLocaleString(
                      'en-US',
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}`,
                  })),
                ]}
                schema={[
                  {
                    header: 'Period',
                    accessorKey: 'period',
                    type: 'string',
                    priority: 0,
                    cardTitle: true,
                  },
                  {
                    header: 'Signups',
                    accessorKey: 'signups',
                    type: 'number',
                    priority: 1,
                  },
                  {
                    header: 'Completed KYC',
                    accessorKey: 'completedKyc',
                    type: 'number',
                    priority: 2,
                  },
                  {
                    header: 'Transaction Volume',
                    accessorKey: 'transactionVolumeFormatted',
                    type: 'string',
                    priority: 3,
                  },
                  {
                    header: 'Commission Earned',
                    accessorKey: 'commissionEarnedFormatted',
                    type: 'string',
                    priority: 4,
                  },
                ]}
              />
            </div>
          </div>
        )}
    </div>
  )
}

// Legacy component for existing usage
interface AffiliatePartnerStatsProps {
  affiliateStats?: AffiliateStatsDto
  isLoading: boolean
}

const AffiliatePartnerStats: React.FC<AffiliatePartnerStatsProps> = ({
  affiliateStats,
  isLoading,
}) => {
  return (
    <AffiliateStatsView
      affiliateStats={affiliateStats}
      isLoading={isLoading}
      showTitle={false}
    />
  )
}

export default AffiliatePartnerStats
